import { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Role-based permission and module access
const ROLES = {
  super_admin: {
    permissions: ['all', 'manage_organisations'],
    modules: ['super_admin_dashboard', 'organisations']
  },
  admin: {
    permissions: ['all'],
    modules: ['dashboard', 'properties', 'rooms', 'guests', 'bookings', 'electricity', 'receipts', 'payments', 'daily_hisab', 'reports', 'import', 'users']
  },
  manager: {
    permissions: [
      'view_dashboard', 'view_properties', 'add_properties', 'edit_properties', 'delete_properties',
      'view_rooms', 'add_rooms', 'edit_rooms', 'delete_rooms',
      'view_guests', 'edit_guests', 'add_guests', 'delete_guests',
      'view_bookings', 'edit_bookings', 'add_bookings', 'delete_bookings',
      'view_electricity', 'edit_electricity',
      'view_receipts', 'add_receipts', 'edit_receipts',
      'view_payments', 'add_payments', 'edit_payments',
      'view_daily_hisab', 'edit_daily_hisab',
      'view_reports', 'export_reports',
      'import_data'
    ],
    modules: ['dashboard', 'properties', 'rooms', 'guests', 'bookings', 'electricity', 'receipts', 'payments', 'daily_hisab', 'reports', 'import']
  },
  accountant: {
    permissions: [
      'view_dashboard',
      'view_guests',
      'view_electricity', 'edit_electricity',
      'view_receipts', 'add_receipts', 'edit_receipts',
      'view_payments', 'add_payments', 'edit_payments',
      'view_daily_hisab', 'edit_daily_hisab',
      'view_reports', 'export_reports',
      'import_data'
    ],
    modules: ['dashboard', 'guests', 'electricity', 'receipts', 'payments', 'daily_hisab', 'reports', 'import']
  },
  viewer: {
    permissions: [
      'view_dashboard', 'view_properties', 'view_rooms', 'view_guests',
      'view_bookings', 'view_electricity', 'view_receipts', 'view_payments',
      'view_daily_hisab', 'view_reports'
    ],
    modules: ['dashboard', 'properties', 'rooms', 'guests', 'bookings', 'electricity', 'receipts', 'payments', 'daily_hisab', 'reports']
  }
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('pg_token'));
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      fetchUser();
    } else {
      setLoading(false);
    }
  }, [token]);

  const fetchUser = async () => {
    try {
      const res = await axios.get(`${API}/auth/me`);
      setUser(res.data);
    } catch (err) {
      console.error('Auth error:', err);
      logout();
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    const res = await axios.post(`${API}/auth/login`, { email, password });
    const { token: newToken, user: userData } = res.data;
    localStorage.setItem('pg_token', newToken);
    axios.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
    setToken(newToken);
    setUser(userData);
    return userData;
  };

  const register = async (name, email, password) => {
    const res = await axios.post(`${API}/auth/register`, { name, email, password });
    const { token: newToken, user: userData } = res.data;
    localStorage.setItem('pg_token', newToken);
    axios.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
    setToken(newToken);
    setUser(userData);
    return userData;
  };

  const logout = () => {
    localStorage.removeItem('pg_token');
    delete axios.defaults.headers.common['Authorization'];
    setToken(null);
    setUser(null);
  };

  // Check if user has a specific permission
  const hasPermission = (permission) => {
    if (!user || !user.role) return false;
    const roleData = ROLES[user.role];
    if (!roleData) return false;
    const permissions = roleData.permissions || [];
    return permissions.includes('all') || permissions.includes(permission);
  };

  // Check if user has access to a specific module
  const hasModuleAccess = (module) => {
    if (!user || !user.role) return false;
    const roleData = ROLES[user.role];
    if (!roleData) return false;
    if (roleData.permissions.includes('all')) return true;
    return roleData.modules.includes(module);
  };

  // Check if user has any of the allowed roles
  const hasRole = (allowedRoles) => {
    if (!user || !user.role) return false;
    if (user.role === 'admin') return true;
    return allowedRoles.includes(user.role);
  };

  // Check if user is admin
  const isAdmin = () => user?.role === 'admin';

  // Check if user is super admin
  const isSuperAdmin = () => user?.role === 'super_admin';

  // Check if user can edit a specific module
  const canEdit = (module) => {
    if (!user || !user.role) return false;
    if (user.role === 'admin') return true;
    return hasPermission(`edit_${module}`);
  };

  // Check if user can add to a specific module
  const canAdd = (module) => {
    if (!user || !user.role) return false;
    if (user.role === 'admin') return true;
    return hasPermission(`add_${module}`);
  };

  // Check if user can delete from a specific module
  const canDelete = (module) => {
    if (!user || !user.role) return false;
    if (user.role === 'admin') return true;
    return hasPermission(`delete_${module}`);
  };

  // Check if user can manage users
  const canManageUsers = () => isAdmin();

  // Check if user can import data
  const canImport = () => hasPermission('import_data') || isAdmin();

  // Check if user can export reports
  const canExport = () => hasPermission('export_reports') || isAdmin();

  return (
    <AuthContext.Provider value={{ 
      user, 
      token, 
      loading, 
      login, 
      register, 
      logout,
      hasPermission,
      hasModuleAccess,
      hasRole,
      isAdmin,
      isSuperAdmin,
      canEdit,
      canAdd,
      canDelete,
      canManageUsers,
      canImport,
      canExport
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
